import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User as UserIcon, KeyRound, ArrowLeft, ShieldCheck, Camera, Upload, X } from "lucide-react";
import TelegramConnectCard from "@/components/TelegramConnectCard";

import { useTranslation } from "react-i18next";
export default function Profile() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changing, setChanging] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const onPhotoFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error(t("auth.profile.photo_must_be_under_5_mb"));
    setUploadingPhoto(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.post("/auth/me/photo", { photo_base64: b64 });
      toast.success(t("auth.profile.photo_updated"));
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploadingPhoto(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove your profile photo?")) return;
    try {
      await api.delete("/auth/me/photo");
      toast.success(t("auth.profile.photo_removed"));
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setWhatsappNumber(user.whatsapp_number || "");
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        name: name.trim() || null,
        email: email.trim() || null,
        whatsapp_number: whatsappNumber.trim() || null,
      });
      toast.success(t("auth.profile.profile_updated"));
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) return toast.error(t("auth.profile.password_must_be_at_least_6_characters"));
    if (newPw !== confirmPw) return toast.error("New password and confirmation don't match");
    setChanging(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success(t("auth.profile.password_changed_other_devices_signed_ou"));
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setChanging(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background px-4 py-6 sm:py-10">
      <div className="mx-auto max-w-2xl space-y-4">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="profile-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        {/* Identity card */}
        <Card className="border-border" data-testid="profile-identity-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <UserIcon className="h-4 w-4" />
              </div>
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Photo */}
            <div className="mb-4 flex items-center gap-4 rounded-lg border border-border/60 bg-muted/20 p-3" data-testid="profile-photo-block">
              <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary">
                {user.photo_url ? (
                  <img
                    src={user.photo_url.startsWith("http") ? user.photo_url : `${backendUrl}${user.photo_url}`}
                    alt={user.name}
                    className="h-full w-full object-cover"
                    data-testid="profile-photo-img"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <UserIcon className="h-8 w-8" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground">{t("auth.profile.profile_photo")}</div>
                <div className="text-[11px] text-muted-foreground">Shown on your CSA card + attendance list. Max 5 MB.</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:border-primary/40" data-testid="profile-photo-upload">
                    <Camera className="h-3 w-3" /> {uploadingPhoto ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/*" hidden onChange={(e) => onPhotoFile(e.target.files?.[0])} disabled={uploadingPhoto} />
                  </label>
                  {user.photo_url && (
                    <button type="button" onClick={removePhoto} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-red-600 hover:bg-red-50" data-testid="profile-photo-remove">
                      <X className="h-3 w-3" /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
            <form onSubmit={saveProfile} className="space-y-3">
              <div>
                <Label htmlFor="pf-name">{t("auth.profile.full_name")}</Label>
                <Input id="pf-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="profile-name" className="h-10" />
              </div>
              <div>
                <Label htmlFor="pf-email">{t("auth.profile.email_optional")}</Label>
                <Input id="pf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} data-testid="profile-email" className="h-10" />
                <p className="mt-0.5 text-[11px] text-muted-foreground">{t("auth.profile.used_for_password_reset_optional_google")}</p>
              </div>
              <div>
                <Label htmlFor="pf-wa">{t("auth.profile.whatsapp_number_optional")}</Label>
                <Input id="pf-wa" value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)} maxLength={20} placeholder="e.g. 9876543210 or +919876543210" data-testid="profile-whatsapp" className="h-10" />
                <p className="mt-0.5 text-[11px] text-muted-foreground">{t("auth.profile.overrides_your_login_phone_for_whatsapp")}</p>
              </div>
              <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
                <div className="text-xs">
                  <div className="font-semibold text-muted-foreground">{t("auth.profile.login_phone")}</div>
                  <div className="font-mono">{user.phone}</div>
                </div>
                <div className="text-xs">
                  <div className="font-semibold text-muted-foreground">{t("dealer.role")}</div>
                  <div className="font-mono">{user.role}</div>
                </div>
              </div>
              <Button type="submit" disabled={saving} className="h-10 w-full" data-testid="profile-save">
                {saving ? "Saving…" : "Save profile"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password card */}
        <Card className="border-border" data-testid="profile-password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white">
                <KeyRound className="h-4 w-4" />
              </div>
              Change password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={changePassword} className="space-y-3">
              <div>
                <Label htmlFor="pf-cur">{t("auth.profile.current_password")}</Label>
                <Input id="pf-cur" type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required data-testid="profile-current-pw" className="h-10" />
              </div>
              <div>
                <Label htmlFor="pf-new">{t("auth.profile.new_password_min_6_chars")}</Label>
                <Input id="pf-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6} data-testid="profile-new-pw" className="h-10" />
              </div>
              <div>
                <Label htmlFor="pf-confirm">{t("auth.profile.confirm_new_password")}</Label>
                <Input id="pf-confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required minLength={6} data-testid="profile-confirm-pw" className="h-10" />
              </div>
              <div className="flex items-start gap-2 rounded-md bg-emerald-50 p-2 text-[11px] text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-100">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{t("auth.profile.all_other_devices_sessions_will_be_signe")}</span>
              </div>
              <Button type="submit" disabled={changing || !currentPw || !newPw} className="h-10 w-full" data-testid="profile-change-pw">
                {changing ? "Changing…" : "Change password"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Telegram connect card — available to every role.
            Especially important for salesmen since our WhatsApp Sandbox has a
            72h re-join limit and 5-recipient cap — Telegram is the reliable
            fallback for shift-end nags and other alerts. */}
        <TelegramConnectCard />
      </div>
    </div>
  );
}
